<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import HitRecordCard from './HitRecordCard.vue';
import type { HitRecord } from './caseDetail';
import type { RecordStatuses } from './recordStateStorage';

const props = withDefaults(
  defineProps<{
    records: HitRecord[];
    statuses: RecordStatuses;
    notes?: Record<string, string>;
    focusedId?: string;
    kbEnabled?: boolean;
  }>(),
  { kbEnabled: true }
);

const reviewedCount = computed(
  () =>
    props.records.filter((record) => (props.statuses[record.id] ?? 'unreviewed') !== 'unreviewed')
      .length
);

const emit = defineEmits<{
  'cycle-status': [recordId: string];
  'open-reader': [recordId: string];
  'update-note': [recordId: string, value: string];
}>();

const listEl = ref<HTMLElement>();

watch(
  () => props.focusedId,
  async (focusedId) => {
    if (!focusedId) return;
    await nextTick();
    const cardEl = listEl.value?.querySelector<HTMLElement>(`[data-record-id="${focusedId}"]`);
    cardEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
);
</script>

<template>
  <div
    class="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
    data-test="hit-record-list"
    ref="listEl"
  >
    <div class="flex items-center justify-between border-b border-gray-100 px-4 py-3">
      <span class="text-[13px] font-semibold text-gray-900">Supporting records</span>
      <span class="text-[11.5px] text-gray-400"
        >{{ reviewedCount }} of {{ records.length }} reviewed</span
      >
    </div>
    <HitRecordCard
      v-for="record in records"
      :key="record.id"
      :record="record"
      :note="notes?.[record.id]"
      :status="statuses[record.id]"
      :focused="record.id === focusedId"
      :kb-enabled="kbEnabled"
      @cycle-status="emit('cycle-status', $event)"
      @open-reader="emit('open-reader', $event)"
      @update-note="(recordId, value) => emit('update-note', recordId, value)"
    />
  </div>
</template>
